import { Money } from './cardDetailHeroTypes'

/**
 * Display helpers shared by the two hero variants. The full hero and the pinned
 * compact bar show the same cardholder and the same available/limit figures, so
 * these must not be forked between them — the same reasoning that lifted the
 * status pill into `StatusPill`.
 */

/**
 * The contract carries `cardholder.displayName` and no initials field, so the
 * avatar fallback is derived. Deliberately dumb — first letter of the first two
 * whitespace-separated tokens — rather than trying to be clever about particles,
 * suffixes, or multi-word surnames.
 */
export const initialsFrom = (displayName: string): string =>
  displayName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()

/**
 * Group a revealed PAN for the card face, matching the double-spaced groups the
 * masked state already renders. Purely presentational: nothing derived here is
 * copied, stored, or sent.
 *
 * 15 digits group 4-6-5 (Amex/Diners); everything else groups in fours, which
 * is right for 16 and degrades sanely for the 12–19 the contract allows. A
 * value carrying anything but digits — an unsubstituted vault alias, say — is
 * returned untouched rather than sliced into misleading groups.
 */
export const groupPanForDisplay = (pan: string): string => {
  if (!/^\d+$/.test(pan)) return pan

  const groups =
    pan.length === 15
      ? [pan.slice(0, 4), pan.slice(4, 10), pan.slice(10)]
      : (pan.match(/.{1,4}/g) ?? [pan])

  return groups.join('  ')
}

/**
 * The number of minor-unit decimal places for a currency (2 for USD, 0 for JPY,
 * 3 for BHD). This is a per-currency constant, but reading it means building an
 * `Intl.NumberFormat` — costly to reconstruct every render for each amount — so
 * results are memoized by currency code. Falls back to 2 for any code Intl can't
 * resolve.
 */
const fractionDigitsCache = new Map<string, number>()
export const currencyFractionDigits = (currency: string): number => {
  const cached = fractionDigitsCache.get(currency)
  if (cached !== undefined) return cached
  let digits = 2
  try {
    digits =
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).resolvedOptions().maximumFractionDigits ?? 2
  } catch {
    digits = 2
  }
  fractionDigitsCache.set(currency, digits)
  return digits
}

/**
 * Minor units → major units at the display edge. The scale is currency-aware:
 * most currencies are 2-decimal (÷100), but zero-decimal (JPY) and three-decimal
 * (BHD) currencies are not — a hardcoded ÷100 would misprice them. (Shared by
 * both hero variants, so the compact bar gets the same currency handling as the
 * full summary.)
 */
export const toDisplayAmount = (money: Money): number =>
  money.amountMinor / 10 ** currencyFractionDigits(money.currency)
